import { useEffect, useMemo, useRef, useState } from "react";
import { Copy, GitCompareArrows, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { useDolfinClient } from "@/components/DolfinProvider";
import { apiErrorMessage } from "@/lib/api-error";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { stateLabel } from "@/lib/bill-utils";
import { enumOptions } from "@/lib/enum-options";
import { EnumMultiSelect } from "@/components/EnumMultiSelect";
import { billStateValues, type components } from "@dolfin/api-admin";
import { BillsTable, type BillsListFilters } from "@/components/BillsTable";
import { ToggleChip } from "@/components/ToggleChip";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DatePicker } from "@/components/ui/date-picker";
import { PageContainer, PageHeader } from "@/components/PagePrimitives";
import { cn } from "@/lib/utils";

export type { BillsListFilters } from "@/components/BillsTable";

type BillState = components["schemas"]["BillState"];

// Legacy Ocr* aliases share labels with Extraction* (rename drain only); excluded so the filter never shows
// two identical rows that would silently split the selection.
const LEGACY_BILL_STATES: readonly BillState[] = ["OcrProcessing", "OcrFailed"];

// No `all` option: the multi-select treats an empty selection as "no filter".
const BILL_STATES = enumOptions(
  billStateValues.filter((state) => !LEGACY_BILL_STATES.includes(state)),
  { label: stateLabel },
);

// Tabs set the State filter rather than sit beside it, so the two never disagree; other combinations select no tab.
const QUEUES: { value: string; label: string; states: BillState[] }[] = [
  { value: "all", label: "All bills", states: [] },
  { value: "review", label: "To review", states: ["PendingReview"] },
  { value: "approve", label: "To approve", states: ["NeedsApproval"] },
  { value: "pay", label: "To pay", states: ["Approved", "Scheduled", "PaymentFailed", "PartiallyPaid"] },
  { value: "paid", label: "Paid", states: ["Paid"] },
];
const CUSTOM_QUEUE = "custom";

function queueFor(states: BillState[]): string {
  const key = [...states].sort().join(",");
  return QUEUES.find((q) => [...q.states].sort().join(",") === key)?.value ?? CUSTOM_QUEUE;
}

const MAX_UPLOAD_FILES = 10;

export interface BillsListPageProps {
  onBillClick: (billId: string) => void;
  onMatchClick?: (billId: string) => void;
  onPurchaseOrderClick?: (purchaseOrderId: string) => void;
  onDeliveryNoteClick?: (deliveryNoteId: string) => void;
  /** Called after a successful upload — the new bill's id is passed so the host can route to it. */
  onUploadComplete?: (billId: string) => void;
  /** Initial filters. Updates to filters live in component state. */
  defaultFilters?: BillsListFilters;
  onFiltersChange?: (filters: BillsListFilters) => void;
  enableUpload?: boolean;
}

export function BillsListPage({
  onBillClick,
  onMatchClick,
  onPurchaseOrderClick,
  onDeliveryNoteClick,
  onUploadComplete,
  defaultFilters,
  onFiltersChange,
  enableUpload: enableUploadProp = true,
}: BillsListPageProps) {
  const api = useDolfinClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [count, setCount] = useState<number | null>(null);
  // Bumped to force BillsTable to refetch (e.g. after a multi-file upload).
  const [reloadSignal, setReloadSignal] = useState(0);

  const enableUpload = enableUploadProp;
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ done: number; total: number } | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const [stateFilter, setStateFilter] = useState<BillState[]>(defaultFilters?.states ?? []);
  const [supplierFilter] = useState<string>(defaultFilters?.supplierId ?? "");
  const [dateFrom, setDateFrom] = useState<string>(defaultFilters?.dueDateFrom ?? "");
  const [dateTo, setDateTo] = useState<string>(defaultFilters?.dueDateTo ?? "");
  const [invoiceNumberInput, setInvoiceNumberInput] = useState<string>(defaultFilters?.invoiceNumber ?? "");
  const debouncedInvoiceNumber = useDebouncedValue(invoiceNumberInput);
  const [poNumberInput, setPoNumberInput] = useState<string>(defaultFilters?.normalisedBillPoNumber ?? "");
  const debouncedPoNumber = useDebouncedValue(poNumberInput);
  const [tagInput, setTagInput] = useState<string>(defaultFilters?.tag ?? "");
  const debouncedTag = useDebouncedValue(tagInput);
  // On/off, not tri-state: nobody asks for only the non-duplicates.
  const [duplicatesOnly, setDuplicatesOnly] = useState<boolean>(defaultFilters?.duplicate === true);
  // Off by default: it costs two lookups per page. A display toggle, so local state rather than a URL filter.
  const [showMatch, setShowMatch] = useState(false);

  const filters = useMemo<BillsListFilters>(
    () => ({
      states: stateFilter.length > 0 ? stateFilter : undefined,
      supplierId: supplierFilter || undefined,
      invoiceNumber: debouncedInvoiceNumber || undefined,
      normalisedBillPoNumber: debouncedPoNumber || undefined,
      dueDateFrom: dateFrom || undefined,
      dueDateTo: dateTo || undefined,
      tag: debouncedTag || undefined,
      duplicate: duplicatesOnly ? true : undefined,
    }),
    [
      stateFilter,
      supplierFilter,
      debouncedInvoiceNumber,
      debouncedPoNumber,
      dateFrom,
      dateTo,
      debouncedTag,
      duplicatesOnly,
    ],
  );

  // Ref so a fresh inline callback doesn't re-fire the effect (an emit loop via e.g. setSearchParams).
  const onFiltersChangeRef = useRef(onFiltersChange);
  onFiltersChangeRef.current = onFiltersChange;
  const firstEmit = useRef(true);

  useEffect(() => {
    if (firstEmit.current) {
      // Host already passed these values as defaultFilters; nothing to sync.
      firstEmit.current = false;
      return;
    }
    onFiltersChangeRef.current?.(filters);
  }, [filters]);

  // Never throws, so a batch can tally successes and failures.
  async function uploadBill(
    file: File,
  ): Promise<{ ok: true; id: string } | { ok: false; error: unknown }> {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const { data, error: apiError } = await api.POST("/v1/bills", {
        // openapi-fetch requires bodySerializer pass-through for multipart payloads
        body: formData as unknown as never,
        bodySerializer: (body) => body as BodyInit,
      });

      if (apiError || !data?.id) {
        return { ok: false, error: apiError };
      }
      return { ok: true, id: data.id };
    } catch (e) {
      console.error("Failed to upload bill:", e);
      return { ok: false, error: e };
    }
  }

  async function handleFiles(fileList: FileList) {
    const files = Array.from(fileList);
    if (files.length === 0) return;
    if (files.length > MAX_UPLOAD_FILES) {
      toast.error(`You can upload at most ${MAX_UPLOAD_FILES} documents at once.`);
      return;
    }

    if (files.length === 1) {
      const file = files[0]!;
      setUploading(true);
      try {
        const result = await uploadBill(file);
        if (!result.ok) {
          toast.error(apiErrorMessage(result.error, "Failed to upload bill"));
          return;
        }
        onUploadComplete?.(result.id);
      } finally {
        setUploading(false);
      }
      return;
    }

    setUploading(true);
    setUploadProgress({ done: 0, total: files.length });
    let completed = 0;
    const results = await Promise.all(
      files.map((file) =>
        uploadBill(file).finally(() => {
          completed += 1;
          setUploadProgress({ done: completed, total: files.length });
        }),
      ),
    );
    setUploading(false);
    setUploadProgress(null);

    const succeeded = results.filter((r) => r.ok).length;
    const failed = results.length - succeeded;

    if (succeeded > 0) {
      toast.success(`Uploaded ${succeeded} of ${results.length} bills.`);
    }
    if (failed > 0) {
      const firstError = results.find((r) => !r.ok);
      toast.error(
        apiErrorMessage(
          firstError && !firstError.ok ? firstError.error : undefined,
          `Failed to upload ${failed} ${failed === 1 ? "bill" : "bills"}.`,
        ),
      );
    }

    setReloadSignal((s) => s + 1);
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length > 0) handleFiles(e.dataTransfer.files);
  }

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(true);
  }

  function handleFileInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files && e.target.files.length > 0) handleFiles(e.target.files);
    e.target.value = "";
  }

  const filtersActive =
    !!invoiceNumberInput || !!poNumberInput || !!tagInput || !!dateFrom || !!dateTo || duplicatesOnly;

  function clearFilters() {
    setInvoiceNumberInput("");
    setPoNumberInput("");
    setTagInput("");
    setDateFrom("");
    setDateTo("");
    setDuplicatesOnly(false);
  }

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Payables"
        title="Bills"
        subtitle={
          count !== null
            ? `${count} shown`
            : "Every supplier invoice, from arrival through review, approval and payment."
        }
        actions={
          enableUpload ? (
            <Button size="sm" disabled={uploading} onClick={() => fileInputRef.current?.click()}>
              {uploading ? <Spinner className="size-4" /> : <Upload className="size-4" />}
              Upload bills
            </Button>
          ) : undefined
        }
      />

      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <Tabs
          value={queueFor(stateFilter)}
          onValueChange={(v) => {
            const queue = QUEUES.find((q) => q.value === v);
            if (queue) setStateFilter(queue.states);
          }}
        >
          <TabsList>
            {QUEUES.map((q) => (
              <TabsTrigger key={q.value} value={q.value}>
                {q.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <EnumMultiSelect
          options={BILL_STATES}
          value={stateFilter}
          onChange={setStateFilter}
          placeholder="Any state"
          searchPlaceholder="Search states..."
          className="w-full sm:w-[220px]"
        />
      </div>

      {/* Narrowing filters on one row; the toggles below switch something on rather than narrow by a value. */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Input
          type="text"
          value={invoiceNumberInput}
          onChange={(e) => setInvoiceNumberInput(e.target.value)}
          className="h-9 w-full sm:w-[140px]"
          placeholder="Invoice #"
          aria-label="Invoice number"
        />
        <Input
          type="text"
          value={poNumberInput}
          onChange={(e) => setPoNumberInput(e.target.value)}
          className="h-9 w-full sm:w-[140px]"
          placeholder="PO #"
          aria-label="PO number"
        />
        <Input
          type="text"
          value={tagInput}
          onChange={(e) => setTagInput(e.target.value)}
          className="h-9 w-full sm:w-[120px]"
          placeholder="Tag"
          aria-label="Tag"
        />
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-muted-foreground">Due</span>
          <DatePicker
            value={dateFrom || null}
            onChange={(v) => setDateFrom(v ?? "")}
            placeholder="From"
            className="h-9 w-36"
          />
          <span className="text-xs text-muted-foreground">to</span>
          <DatePicker
            value={dateTo || null}
            onChange={(v) => setDateTo(v ?? "")}
            placeholder="To"
            className="h-9 w-36"
          />
        </div>
        {filtersActive ? (
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            <X className="size-4" /> Clear filters
          </Button>
        ) : null}
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <ToggleChip
          id="bills-duplicates-only"
          icon={Copy}
          label="Only show duplicates"
          checked={duplicatesOnly}
          onCheckedChange={setDuplicatesOnly}
          hint="Narrows the list to the bills flagged as a possible duplicate of an earlier bill, which cannot be approved until you confirm they are separate invoices. Off means no filter, so every bill is listed."
        />
        <ToggleChip
          id="bills-show-match"
          icon={GitCompareArrows}
          label="Match"
          checked={showMatch}
          onCheckedChange={setShowMatch}
          hint="Adds a column showing each bill's three-way match status and every purchase order and delivery note it is matched to. Off by default: it costs an extra lookup per page and only applies if you raise purchase orders."
        />
      </div>

      {enableUpload && (
        <div
          className={cn(
            "mb-4 rounded-xl border border-dashed px-4 py-3 transition-colors",
            dragOver ? "border-accent-blue/60 bg-accent-blue/5" : "bg-card/30 hover:border-muted-foreground/40",
            uploading ? "pointer-events-none opacity-60" : "cursor-pointer",
          )}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={() => setDragOver(false)}
          onClick={() => !uploading && fileInputRef.current?.click()}
        >
          {uploading ? (
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Spinner className="size-4" />
              {uploadProgress
                ? `Uploading ${uploadProgress.done} of ${uploadProgress.total} bills...`
                : "Uploading bill..."}
            </div>
          ) : (
            <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-sm">
              <Upload className="size-4 text-muted-foreground" />
              <span className="font-medium">Drop PDFs or images here, or click to browse</span>
              <span className="text-xs text-muted-foreground">Up to {MAX_UPLOAD_FILES} files · PDF · PNG · JPG</span>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.png,.jpg,.jpeg"
            multiple
            className="hidden"
            onChange={handleFileInputChange}
          />
        </div>
      )}

      <BillsTable
        filters={filters}
        onBillClick={onBillClick}
        onMatchClick={onMatchClick}
        includeMatch={showMatch}
        onPurchaseOrderClick={onPurchaseOrderClick}
        onDeliveryNoteClick={onDeliveryNoteClick}
        reloadSignal={reloadSignal}
        onCountChange={setCount}
      />
    </PageContainer>
  );
}
